import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { ChartNoAxesColumn, ChevronLeft, ChevronRight, House, LogOut, Mic, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { signOutUser } from "@/lib/signOut";
import { useIsMobile } from "@/hooks/use-mobile";
import MobileNav from "@/components/MobileNav";
import { Wordmark, WordmarkMark } from "@/components/landing/Wordmark";
import { useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { APP_NAV, APP_SIDEBAR_EXTRA } from "@/navigation";

const ICONS = {
  "/app": House,
  "/app/practice": Mic,
  "/app/progress": ChartNoAxesColumn,
  "/app/upgrade": Sparkles,
} as const;

const Layout = () => {
  const location = useLocation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { isPaid } = useSubscription();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const signingOutRef = useRef(false);

  const handleLogout = async () => {
    if (signingOutRef.current) return;
    signingOutRef.current = true;
    setSigningOut(true);
    const { error } = await signOutUser();
    if (error) {
      signingOutRef.current = false;
      setSigningOut(false);
      toast({
        title: "Erro ao sair",
        description: error.message,
        variant: "destructive",
      });
      return;
    }
    navigate("/auth");
  };

  const isActive = (path: string) => {
    if (path === "/app") {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <TooltipProvider>
      <div className="flex min-h-screen w-full flex-col bg-neutral-50 md:flex-row">
        {!isMobile && (
          <aside
            className={cn(
              "sticky top-0 hidden h-screen shrink-0 flex-col border-r border-neutral-200 bg-white transition-all duration-300 md:flex",
              sidebarCollapsed ? "w-20" : "w-64",
            )}
          >
            <Button
              variant="ghost"
              size="icon"
              className="absolute -right-3 top-6 z-10 h-6 w-6 rounded-full border bg-white shadow-md"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            >
              {sidebarCollapsed ? (
                <ChevronRight className="h-3 w-3" />
              ) : (
                <ChevronLeft className="h-3 w-3" />
              )}
            </Button>

            <div className={cn("border-b border-neutral-200", sidebarCollapsed ? "p-4" : "p-6")}>
              {sidebarCollapsed ? (
                <div className="flex justify-center">
                  <WordmarkMark />
                </div>
              ) : (
                <Wordmark to="/app" />
              )}
            </div>

            <nav className={cn("flex-1 space-y-1 overflow-y-auto", sidebarCollapsed ? "p-2" : "p-4")}>
              {[
                ...APP_NAV,
                {
                  path: APP_SIDEBAR_EXTRA.path,
                  label: isPaid ? APP_SIDEBAR_EXTRA.labelPaid : APP_SIDEBAR_EXTRA.labelFree,
                },
              ].map((item) => {
                const Icon = ICONS[item.path as keyof typeof ICONS];
                return (
                  <Tooltip key={item.path} delayDuration={0}>
                    <TooltipTrigger asChild>
                      <Link to={item.path}>
                        <Button
                          variant={isActive(item.path) ? "default" : "ghost"}
                          className={cn(
                            "w-full rounded-xl",
                            sidebarCollapsed ? "justify-center px-2" : "justify-start gap-3",
                          )}
                        >
                          <Icon className="h-5 w-5" strokeWidth={1.75} />
                          {!sidebarCollapsed && <span>{item.label}</span>}
                        </Button>
                      </Link>
                    </TooltipTrigger>
                    {sidebarCollapsed && (
                      <TooltipContent side="right">{item.label}</TooltipContent>
                    )}
                  </Tooltip>
                );
              })}
            </nav>

            <div className={cn("border-t border-neutral-200", sidebarCollapsed ? "p-2" : "p-4")}>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    aria-label="Sair"
                    loading={signingOut}
                    className={cn(
                      "w-full rounded-xl text-destructive hover:bg-destructive/10 hover:text-destructive",
                      sidebarCollapsed ? "justify-center px-2" : "justify-start gap-3",
                    )}
                    onClick={handleLogout}
                  >
                    <LogOut className={sidebarCollapsed ? "h-5 w-5" : "h-5 w-5"} />
                    {!sidebarCollapsed && <span>{signingOut ? "Saindo…" : "Sair"}</span>}
                  </Button>
                </TooltipTrigger>
                {sidebarCollapsed && <TooltipContent side="right">Sair</TooltipContent>}
              </Tooltip>
            </div>
          </aside>
        )}

        <main className="min-w-0 flex-1 overflow-auto pb-24 md:pb-0">
          <Outlet />
        </main>

        {isMobile && <MobileNav />}
      </div>
    </TooltipProvider>
  );
};

export default Layout;
